<template>
  <CCard class="mt-4" v-if="report">
    <CCardHeader
      style="background-color: #145654"
      class="text-white d-flex justify-content-between align-items-center"
    >
      <h4 class="mb-0">
        <CIcon name="cil-description" /> {{ $t('ticketDetail.tech.pageTitle') }}
      </h4>
      <CButton color="light" size="sm" @click="$emit('close')">
        <CIcon name="cil-x" />
      </CButton>
    </CCardHeader>

    <CCardBody class="p-4">
      <div class="work-order-timeline my-4">
        <div class="timeline-line" :style="getTimelineStyle()"></div>
        <div
          v-for="step in timelineSteps"
          :key="step.num"
          :class="['timeline-step', { active: getCurrentStep() >= step.num }]"
        >
          <div v-if="step.num === 1 && getCurrentStep() === 1" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/bb3a855a-a6d2-443c-b6a9-523a0a416476/jYe7PDPdCV.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 2 && getCurrentStep() === 2" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/ebf1b394-df01-458b-a3eb-dd0129aceab2/m4Y81rb9Rp.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 3 && getCurrentStep() === 3" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/b1900545-252b-4418-af8e-2f8427524719/v4NfdUTB1W.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 4 && report.status === 'done'" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/6078979a-4896-49cf-9f62-964f5bf12ad4/9eGbn1SSa9.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div class="step-circle"></div>
          <div class="step-label">
            <div class="step-status">{{ step.status }}</div>
            <div class="step-role">{{ step.role }}</div>
          </div>
        </div>
      </div>

      <div v-if="report.status === 'assigned' && !amIReporter" class="action-box text-center p-4 mb-4">
        <h5 class="font-weight-bold text-info mb-3">{{ $t('ticketDetail.tech.newJobTitle') }}</h5>
        <p class="text-muted">{{ $t('ticketDetail.tech.newJobHint') }}</p>
        <div class="d-flex justify-content-center flex-wrap" style="gap: 12px;">
          <CButton
            color="info"
            class="text-white px-4 shadow-sm"
            @click="acceptWork"
            :disabled="isProcessing"
          >
            <CIcon name="cil-check-circle" /> 
            {{ isProcessing ? $t('ticketDetail.saving') : $t('ticketDetail.tech.acceptJob') }}
          </CButton>
          <CButton
            color="danger"
            variant="outline"
            class="px-4 shadow-sm"
            @click="rejectWork"
            :disabled="isProcessing"
          >
            <CIcon name="cil-x-circle" /> 
            {{ $t('ticketDetail.tech.rejectJob') }}
          </CButton>
        </div>
      </div>

      <div v-if="report.status === 'in_progress' && !amIReporter" class="action-box p-4 mb-4">
        <h5 class="font-weight-bold text-success mb-3 text-center">{{ $t('ticketDetail.tech.closeSection') }}</h5>
        
        <CForm @submit.prevent="finishWork">
          
          <div class="form-group">
            <label class="font-weight-bold text-dark">{{ $t('ticketDetail.tech.repairDetailLabel') }}</label>
            <CTextarea 
              v-model="maintenanceDetails" 
              rows="4" 
              :placeholder="$t('ticketDetail.tech.repairPlaceholder')"
              required
              class="mb-3"
            />
          </div>

          <div class="form-group">
            <label class="font-weight-bold text-dark">{{ $t('ticketDetail.tech.photoAfterRequired') }}</label>
            <div class="custom-file">
              <input 
                type="file" 
                class="custom-file-input" 
                id="afterFixImage"
                accept="image/*"
                @change="handleFileUpload"
                required
              >
              <label class="custom-file-label" for="afterFixImage">
                {{ selectedFile ? selectedFile.name : $t('ticketDetail.tech.choosePhoto') }}
              </label>
            </div>
            <small v-if="uploadError" class="text-danger mt-1 d-block">{{ uploadError }}</small>
          </div>

          <div class="text-center mt-4">
            <CButton 
              type="submit" 
              color="success" 
              class="text-white px-5"
              :disabled="isProcessing"
            >
              <span v-if="!isProcessing">{{ $t('ticketDetail.tech.confirmClose') }}</span>
              <span v-else>{{ $t('ticketDetail.tech.uploading') }}</span>
            </CButton>
          </div>
        </CForm>
      </div>

      <!-- แสดงสถานะสำหรับ ticket ที่ช่างเป็นผู้แจ้งเอง -->
      <div v-if="amIReporter && ['pending','assigned','in_progress'].includes(report.status)" class="text-center p-3 mb-4 bg-light border rounded">
        <span class="badge badge-reporter-status mr-2">{{ $t('ticketDetail.tech.selfReporterBadge') }}</span>
        <span class="text-muted">{{ $t('ticketDetail.tech.statusLine') }} {{ getStatusTextFull(report.status) }}</span>
      </div>

      <!-- ประเมินความพึงพอใจ -- แสดงเมื่อช่างเป็นผู้แจ้งเองและสถานะ done/welldone -->
      <div v-if="amIReporter && ['done','welldone'].includes(report.status)" class="evaluation-box text-center p-4 mb-4">
        <h5 class="font-weight-bold text-dark mb-2">
          {{ report.status === 'done' ? $t('ticketDetail.evalPlease') : $t('ticketDetail.evalResult') }}
        </h5>
        <div class="star-rating mb-3">
          <span
            v-for="star in 5"
            :key="star"
            class="star"
            :class="{
              filled: star <= (hoverRating || rating),
              disabled: report.status === 'welldone',
            }"
            @mouseover="report.status === 'done' ? (hoverRating = star) : null"
            @mouseleave="report.status === 'done' ? (hoverRating = 0) : null"
            @click="report.status === 'done' ? (rating = star) : null"
          >★</span>
        </div>
        <div v-if="rating > 0" class="mb-3 text-warning font-weight-bold">
          {{ getRatingText(rating) }}
        </div>
        <CButton
          v-if="report.status === 'done'"
          color="success"
          size="lg"
          @click="submitEvaluation"
          :disabled="isSubmittingRating || rating === 0"
          class="px-5 shadow-sm"
        >
          <span v-if="!isSubmittingRating">{{ $t('ticketDetail.evalSubmit') }}</span>
          <span v-else>{{ $t('ticketDetail.saving') }}</span>
        </CButton>
      </div>

      <!-- แสดงสถานะสำหรับ ticket ที่ช่างเป็นผู้รับงาน (ไม่ใช่ผู้แจ้ง) -->
      <div v-if="!amIReporter && report.status === 'done'" class="text-center p-3 mb-4 bg-light border rounded">
         <h5 class="text-success m-0">{{ $t('ticketDetail.tech.waitReporterEval') }}</h5>
      </div>
      
      <div v-if="report.status === 'welldone' && !amIReporter" class="text-center p-3 mb-4 bg-success-light border border-success rounded">
         <h5 class="text-success m-0">{{ $t('ticketDetail.tech.jobWelldone') }}</h5>
      </div>

      <hr class="border-dashed my-4" />

      <div class="mx-3">
          <div class="mb-4 p-3 bg-white border rounded shadow-sm">
             <h6 class="font-weight-bold mb-3 text-primary border-bottom pb-2">{{ $t('ticketDetail.assignees') }}</h6>
             <CRow>
               <CCol sm="6">
                  <label class="lbl">{{ $t('ticketDetail.supervisorAssigner') }}</label>
                  <div>{{ getSupervisorName() }}</div>
                  <small class="text-muted" v-if="report.supervisorPart && report.supervisorPart.dateTimeInformed">
                     {{ $t('ticketDetail.tech.supervisorWhen') }} {{ formatDate(report.supervisorPart.dateTimeInformed) }}
                  </small>
               </CCol>
               <CCol sm="6">
                  <label class="lbl">{{ $t('ticketDetail.tech.youTechnician') }}</label>
                  <div class="text-primary font-weight-bold">{{ getTechnicianName() }}</div>
                  <small class="text-muted" v-if="getCompletionDate()">
                     {{ $t('ticketDetail.finishedWhen') }} {{ formatDate(getCompletionDate()) }}
                  </small>
               </CCol>
             </CRow>
          </div>

          <h5 class="font-weight-bold mb-4">{{ $t('ticketDetail.sectionReport') }}</h5>

          <CRow class="mb-2">
            <CCol sm="12">
              <div class="p-3 bg-light border rounded d-flex align-items-center">
                <div class="mr-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width:40px; height:40px;">
                  <CIcon name="cil-tag" />
                </div>
                <div>
                  <label class="lbl mb-0 text-muted">{{ $t('ticketDetail.ticketNo') }}</label>
                  <div class="text-primary font-weight-bold h4 mb-0">
                    {{ report.ticketNumber || '-' }}
                  </div>
                </div>
              </div>
            </CCol>
          </CRow>

          <div class="mb-4 mt-2">
            <CRow class="mb-3">
              <CCol sm="6" md="3" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.reporter') }}</label>
                <div class="text-dark">{{ getUserName() }} <span v-if="getOnBehalfOf()" class="text-danger font-weight-bold ml-1" style="font-size: 0.9em;">({{ $t('ticketDetail.onBehalf') }} {{ getOnBehalfOf() }})</span></div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.deptCombined') }}</label>
                <div class="text-dark">{{ getDepartment() }}</div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.phonePersonalShort') }}</label>
                <div class="text-dark">{{ getPersonalPhone() }}</div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.phoneWorkShort') }}</label>
                <div class="text-dark">{{ getOfficePhone() }}</div>
              </CCol>
            </CRow>
            <CRow>
              <CCol sm="6" md="3" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.jobType') }}</label>
                <div class="text-dark">{{ getCategoryName() }}</div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.workArea') }}</label>
                <div class="text-primary font-weight-bold">{{ getLocationFull() }}</div>
              </CCol>
              <CCol sm="12" md="6" class="mb-3">
                <label class="lbl mb-1">{{ $t('ticketDetail.timeSlot') }}</label>
                <div class="text-dark">{{ getAvailableTime() }}</div>
              </CCol>
            </CRow>
          </div>

          <CRow v-if="isOutdoor()" class="mb-4">
            <CCol>
              <label class="lbl">{{ $t('ticketDetail.mapPoint') }}</label>
              <div class="map-container border rounded shadow-sm overflow-hidden" style="height: 350px;">
                <div ref="mapContainer" style="width: 100%; height: 100%"></div>
              </div>
              <div class="mt-2 text-right">
                  <a :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" target="_blank" class="btn btn-sm btn-outline-primary">
                      <CIcon name="cil-map" /> {{ $t('ticketDetail.openMaps') }}
                  </a>
              </div>
            </CCol>
          </CRow>

          <CRow v-if="getMachineNumberImage()" class="mb-4">
            <CCol>
              <label class="lbl">{{ $store.state.locale.lang === 'en' ? 'Machine number / asset number photo' : 'รูปหมายเลขเครื่อง / เลขครุภัณฑ์' }}</label>
              <div class="text-center bg-light p-2 rounded border">
                <img :src="getImageUrl(getMachineNumberImage())" class="img-fluid rounded shadow-sm" style="max-height: 300px; cursor: zoom-in;" @click="openImage(getImageUrl(getMachineNumberImage()))" />
              </div>
            </CCol>
          </CRow>

          <CRow class="mb-3">
            <CCol>
              <label class="lbl">{{ $t('ticketDetail.tech.problemLabel') }}</label>
              <div class="p-3 bg-light rounded border" v-html="getProblemDetails()"></div>
            </CCol>
          </CRow>

          <CRow v-if="getProblemImage()" class="mb-4">
            <CCol>
              <label class="lbl">{{ $t('ticketDetail.photoBefore') }}</label>
              <div class="text-center bg-light p-2 rounded border">
                 <img :src="getImageUrl(getProblemImage())" class="img-fluid rounded shadow-sm" style="max-height: 300px; cursor: zoom-in;" @click="openImage(getImageUrl(getProblemImage()))" />
              </div>
            </CCol>
          </CRow>

          <div v-if="['done', 'welldone'].includes(report.status)">
             <h5 class="font-weight-bold mb-3 mt-5 text-success">{{ $t('ticketDetail.workOutcome') }}</h5>
             
             <CRow class="mb-3">
                <CCol>
                   <label class="lbl">{{ $t('ticketDetail.fixDetail') }}</label>
                   <div class="p-3 bg-success-light rounded border border-success">
                      {{ getMaintenanceDetails() || '-' }}
                   </div>
                </CCol>
             </CRow>

             <CRow v-if="getMaintenanceImage()">
                <CCol>
                   <label class="lbl">{{ $t('ticketDetail.photoAfter') }}</label>
                   <div class="text-center bg-light p-2 rounded border">
                      <img :src="getImageUrl(getMaintenanceImage())" class="img-fluid rounded shadow-sm" style="max-height: 300px; cursor: zoom-in;" @click="openImage(getImageUrl(getMaintenanceImage()))" />
                   </div>
                </CCol>
             </CRow>
          </div>

      </div>

      <CModal :show.sync="imageModal" centered size="lg">
        <img :src="selectedImageUrl" class="img-fluid w-100" />
        <template #footer><CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') }}</CButton></template>
      </CModal>

    </CCardBody>
  </CCard>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import reportDetailMixin from "../../i18n/reportDetailMixin";
import { formatAvailableTimeDisplay } from "../../i18n/formatAvailableTime";

export default {
  name: "ReportDetail",
  mixins: [reportDetailMixin],
  props: { 
    report: { type: Object, default: null },
  },
  data() {
    return {
      isProcessing: false,
      selectedFile: null,
      uploadError: "",
      maintenanceDetails: "", 
      imageModal: false,
      selectedImageUrl: null,
      map: null,
      marker: null,
      // สำหรับการประเมิน (ใช้เมื่อ isReporter === true)
      rating: 5,
      hoverRating: 0,
      isSubmittingRating: false,
    };
  },
  created() {
    // ถ้าเป็น welldone และมีการประเมินแล้ว ให้แสดงคะแนนนั้น
    if (this.report && this.report.status === 'welldone' && this.report.rating) {
      this.rating = this.report.rating.stars || 5;
    }
  },
  computed: {
    ...mapGetters("auth", ["profile"]),
    // คำนวณว่าช่างเป็นผู้แจ้งเองหรือไม่
    amIReporter() {
      if (!this.profile || !this.report) return false;
      const myId = String(this.profile._id);
      const reporterId = this.report.userPart?.user?._id
        ? String(this.report.userPart.user._id)
        : this.report.userPart?.user
          ? String(this.report.userPart.user)
          : null;
      return myId === reporterId;
    },
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation;
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude),
        };
      }
      return { lat: 0, lng: 0 };
    },
  },
  watch: {
    report: {
      deep: true,
      handler(val) {
        if (this.isOutdoor()) {
          this.$nextTick(() => {
            this.initMap();
          });
        }
      }
    }
  },
  mounted() {
    if (this.isOutdoor()) {
      this.$nextTick(() => {
        this.initMap();
      });
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map = null;
      this.marker = null;
    }
  },
  methods: {
    async submitEvaluation() {
      if (!this.rating || this.rating < 1) {
        return;
      }
      this.isSubmittingRating = true;
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/rating`,
          { stars: this.rating }
        );
        if (res.data.success || res.status === 200) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('ticketDetail.swalEvalOk'),
            confirmButtonColor: '#2ba9a5',
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh');
          this.$emit('close');
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: 'error', title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.swalSaveFail') });
      } finally {
        this.isSubmittingRating = false;
      }
    },
    async acceptWork() {
      const result = await Swal.fire({
        icon: "question",
        title: this.$t('ticketDetail.tech.acceptConfirmTitle'),
        text: this.$t('ticketDetail.tech.acceptConfirmText'),
        showCancelButton: true,
        confirmButtonText: this.$t('ticketDetail.tech.acceptConfirmBtn'),
        cancelButtonText: this.$t('mgmt.cancel'),
        confirmButtonColor: "#2ba9a5",
        cancelButtonColor: "#adb5bd",
      });

      if (!result.isConfirmed) return;

      this.isProcessing = true;
      try {
        const res = await axios.put(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/accept`,
          { technicianId: this.profile._id }
        );

        if (res.data.success) {
          this.report.status = "in_progress";
          this.$emit("refresh");
          Swal.fire({
            icon: "success",
            title: this.$t('ticketDetail.tech.acceptOkTitle'),
            text: this.$t('ticketDetail.tech.acceptOkText'),
            timer: 1600,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.tech.acceptFail') });
      } finally {
        this.isProcessing = false;
      }
    },
    async rejectWork() {
      const { value: reason, isConfirmed } = await Swal.fire({
        title: this.$t('ticketDetail.tech.rejectConfirmTitle'),
        text: this.$t('ticketDetail.tech.rejectConfirmText'),
        input: 'textarea',
        inputLabel: this.$t('ticketDetail.tech.rejectedReasonLabel') || 'เหตุผลการปฏิเสธงาน',
        inputPlaceholder: this.$t('ticketDetail.tech.rejectReasonPlaceholder'),
        inputAttributes: {
          'aria-label': this.$t('ticketDetail.tech.rejectReasonPlaceholder'),
          rows: 3
        },
        showCancelButton: true,
        confirmButtonText: this.$t('ticketDetail.tech.rejectConfirmBtn'),
        cancelButtonText: this.$t('mgmt.cancel'),
        confirmButtonColor: "#e55353",
        cancelButtonColor: "#adb5bd",
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return this.$t('ticketDetail.tech.rejectReasonRequired');
          }
        }
      });

      if (!isConfirmed || !reason) return;

      this.isProcessing = true;
      try {
        const res = await axios.put(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/reject`,
          {
            technicianId: this.profile._id,
            reason: reason.trim()
          }
        );

        if (res.data.success) {
          this.report.status = "pending";
          this.$emit("refresh");
          this.$emit("close");
          Swal.fire({
            icon: "success",
            title: this.$t('ticketDetail.tech.rejectOkTitle'),
            text: this.$t('ticketDetail.tech.rejectOkText'),
            timer: 1800,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error("Reject ticket error:", error);
        const errMsg = error.response?.data?.message || this.$t('ticketDetail.tech.rejectFail');
        Swal.fire({
          icon: "error",
          title: this.$t('ticketDetail.swalError'),
          text: errMsg
        });
      } finally {
        this.isProcessing = false;
      }
    },


    compressImage(file, quality = 0.8, maxWidth = 1200) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxWidth) {
                width *= maxWidth / height;
                height = maxWidth;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Compression failed'));
                return;
              }
              
              // We'll return the blob and the suggested name separately if needed, 
              // or just use File if supported.
              try {
                const name = file.name ? file.name.replace(/\.[^/.]+$/, ".jpg") : "photo.jpg";
                const compressedFile = new File([blob], name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } catch (e) {
                // Fallback to Blob if File constructor is not supported
                // We'll attach the name later in FormData.append
                resolve(blob);
              }
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
      });
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      this.uploadError = "";
      
      if (!file) {
        this.selectedFile = null;
        return;
      }

      // Check if it's an image
      if (!file.type || !file.type.startsWith('image/')) {
        this.uploadError = "กรุณาเลือกไฟล์รูปภาพเท่านั้น"; 
        this.selectedFile = null;
        event.target.value = "";
        return;
      }

      this.isProcessing = true;
      try {
        const compressedFile = await this.compressImage(file);
        this.selectedFile = compressedFile;
      } catch (error) {
        console.error("Compression error:", error);
        // Fallback to original
        this.selectedFile = file;
      } finally {
        this.isProcessing = false;
      }
    },

    async finishWork() {
      if (!this.selectedFile) {
        Swal.fire({
          icon: "warning",
          title: this.$t('ticketDetail.tech.needPhotoTitle'),
          text: this.$t('ticketDetail.tech.needPhotoText'),
          confirmButtonColor: "#f9b115",
        });
        return;
      }
      if (!this.maintenanceDetails || !this.maintenanceDetails.trim()) {
        Swal.fire({
          icon: "warning",
          title: this.$t('ticketDetail.tech.needDetailTitle'),
          text: this.$t('ticketDetail.tech.needDetailText'),
          confirmButtonColor: "#f9b115",
        });
        return;
      }

      const result = await Swal.fire({
        icon: "question",
        title: this.$t('ticketDetail.tech.closeConfirmTitle'),
        text: this.$t('ticketDetail.tech.closeConfirmText'),
        showCancelButton: true,
        confirmButtonText: this.$t('mgmt.confirm'),
        cancelButtonText: this.$t('mgmt.cancel'),
        confirmButtonColor: "#2ba9a5",
        cancelButtonColor: "#adb5bd",
      });

      if (!result.isConfirmed) return;

      this.isProcessing = true;
      try {
        const formData = new FormData();
        
        // Determine filename
        let fileName = "maintenance.jpg";
        if (this.selectedFile.name) {
          fileName = this.selectedFile.name;
        }

        formData.append("maintenanceImage", this.selectedFile, fileName);
        formData.append("maintenanceDetails", this.maintenanceDetails);
        if (this.profile && this.profile._id) {
          formData.append("technicianId", this.profile._id);
        }

        const res = await axios.put(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/finish`,
          formData
        );

        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: this.$t('ticketDetail.tech.closeOkTitle'),
            text: this.$t('ticketDetail.tech.closeOkText'),
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit("refresh");
        } else {
          throw new Error(res.data.message || res.data.error || "Failed to finish ticket");
        }
      } catch (error) {
        console.error("Finish work error:", error);
        const errMsg = error.response?.data?.message || error.response?.data?.error || this.$t('ticketDetail.tech.closeFail');
        Swal.fire({ 
          icon: "error", 
          title: this.$t('ticketDetail.swalError'), 
          text: errMsg 
        });
      } finally {
        this.isProcessing = false;
      }
    },

    openImage(url) {
      this.selectedImageUrl = url;
      this.imageModal = true;
    },

    // --- Helpers ---
    getCurrentStep() {
      if (!this.report) return 1;
      const map = { pending: 1, assigned: 2, in_progress: 3, done: 4, welldone: 4 };
      return map[this.report.status] || 1;
    },
    getStatusTextFull(status) {
      void this.$store.state.locale.lang;
      const key = `ticketDetail.statusFull.${status}`;
      const t = this.$t(key);
      return t !== key ? t : status;
    },
    getTimelineStyle() {
      const p = { 1: 0, 2: 33, 3: 66, 4: 100 }[this.getCurrentStep()] || 0;
      return { background: `linear-gradient(to right, #007bff ${p}%, #e0e0e0 ${p}%)` };
    },
    getUserName() {
      const u = this.report?.userPart?.user;
      return u ? `${u.name} ${u.surname || ''}` : this.$t('ticketDetail.noName');
    },

    getOnBehalfOf() {
      if (this.report && this.report.onBehalfOf) return this.report.onBehalfOf;
      return this.report && this.report.userPart && this.report.userPart.onBehalfOf;
    },
    getDepartment() { return this.report?.userPart?.category?.departmentName || "-"; },
    getPersonalPhone() { return this.report?.userPart?.personalPhone || "-"; },
    getOfficePhone() { return this.report?.userPart?.telephoneWork || this.report?.userPart?.user?.telephoneWork || "-"; },
    getAvailableTime() {
      const raw = this.report?.userPart?.availableTime;
      if (!raw) return "-";
      void this.$store.state.locale.lang;
      const lang = this.$store.state.locale.lang === "en" ? "en" : "th";
      return formatAvailableTimeDisplay(raw, lang) + this.$t("ticketDetail.timeSuffix");
    },
    
    getCategoryName() { 
        return this.report.userPart?.subCategory || "-"; 
    },
    
    getLocationFull() {
      const u = this.report.userPart;
      if (!u) return "-";
      if (u.locationType === "building") {
        let bName = null;
        const building = u.buildingLocation?.building;
        if (building) {
          if (typeof building === 'object') {
            bName = building.BUILDINGName || building.BUILDINGCODE || building.name || null;
          } else {
            bName = building;
          }
        }
        const rName = u.buildingLocation?.roomName || '-';
        if (bName) {
          return this.$t('ticketDetail.locationBuilding').replace('{b}', bName).replace('{r}', rName);
        }
        // Parse roomName (e.g. "test-04") into building / room
        const dashIdx = rName.indexOf('-');
        if (dashIdx > 0) {
          const parsedBuilding = rName.substring(0, dashIdx).trim();
          const parsedRoom = rName.substring(dashIdx + 1).trim();
          return this.$t('ticketDetail.locationBuilding').replace('{b}', parsedBuilding).replace('{r}', parsedRoom);
        }
        return rName;
      } else {
        return u.locationDetail || this.$t('ticketDetail.noLocationDetail');
      }
    },
    getProblemDetails() { return this.report.userPart?.problemDetails || "-"; },
    getMachineNumberImage() { return this.report.userPart?.machineNumberImage; },
    getProblemImage() { return this.report.userPart?.problemImage; },
    getCompletionDate() { return this.report.technicianPart?.dateTimeFinished; },
    getSupervisorName() {
      const s = this.report?.supervisorPart?.supervisor;
      return s ? `${s.name} ${s.surname || ''}` : this.$t('table.tech.supervisorFallback');
    },
    getTechnicianName() {
      const t = this.report?.supervisorPart?.assignedTechnician;
      if (t && typeof t === 'object') {
        return `${t.name} ${t.surname}`;
      }
      return `${this.profile.name} ${this.profile.surname}`;
    },
    getMaintenanceImage() { return this.report?.technicianPart?.maintenanceImage; },
    getMaintenanceDetails() { return this.report?.technicianPart?.maintenanceDetails; },
    
    getImageUrl(f) {
      if (!f) return "";
      const filename = f.split("/").pop();
      return `${process.env.VUE_APP_APP_URL}/uploads/${filename}`;
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const d = new Date(dateString);
      const loc = this.$store.state.locale.lang === "en" ? "en-US" : "th-TH";
      return d.toLocaleString(loc);
    },
    isOutdoor() {
      return (
        this.report?.userPart?.locationType === "outdoor" &&
        this.report?.userPart?.outdoorLocation?.latitude &&
        this.report?.userPart?.outdoorLocation?.longitude
      );
    },
    initMap() {
      if (!this.isOutdoor() || !this.$refs.mapContainer) return;
      
      // ตรวจสอบว่า Google Maps API โหลดแล้วหรือยัง
      if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
        console.error('Google Maps API is not loaded. Please check script tag in index.html');
        setTimeout(() => this.initMap(), 100);
        return;
      }

      const position = { lat: this.mapPosition.lat, lng: this.mapPosition.lng };
      
      // สร้างแผนที่ Google Maps
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: position,
        zoom: 16,
        mapTypeId: google.maps.MapTypeId.HYBRID
      });

      // เพิ่ม marker
      this.marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: this.$t('ticketDetail.mapPinTitle')
      });
    }
  },
};
</script>

<style scoped>
.action-box {
  background-color: #fff;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.badge-reporter-status {
  display: inline-block;
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}

.bg-success-light {
  background-color: #d4edda;
  color: #155724;
}

.lbl {
  font-weight: 700;
  color: #495057;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.work-order-timeline {
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}

.timeline-line {
  position: absolute;
  top: 15px;
  left: 12%;
  right: 12%;
  height: 3px;
  z-index: 0;
  background: #e0e0e0;
}

.timeline-step {
  position: relative;
  z-index: 1;
  text-align: center;
  width: 25%;
}

.step-circle {
  width: 30px;
  height: 30px;
  background: #fff;
  border: 4px solid #e0e0e0;
  border-radius: 50%;
  margin: 0 auto 5px;
}

.active .step-circle {
  border-color: #007bff;
  background: #007bff;
}

.step-status {
  font-weight: bold;
  font-size: 0.85rem;
}

.step-role {
  font-size: 0.75rem;
  color: #888;
}
:deep(.gm-style) {
  height: 100%;
  width: 100%;
  z-index: 0;
}

/* ส่วนประเมิน (isReporter mode) */
.evaluation-box {
  background-color: #fff8e1;
  border: 2px dashed #ffc107;
  border-radius: 10px;
}
.star-rating {
  font-size: 2.5rem;
  line-height: 1;
  cursor: pointer;
}
.star {
  color: #e4e5e9;
  transition: color 0.2s;
  margin: 0 5px;
}
.star.filled { color: #ffc107; }
.star.disabled { cursor: default; }
.star:not(.disabled):hover { transform: scale(1.1); }
</style>
